import { Loader2 } from "lucide-react";
import { Stack } from "@/core/components/ui/stack";
import { Switch } from "@/core/components/ui/switch";
import { cn } from "@/core/utils/utils";
import type { IPayrollSheetApprovalAccessKey } from "@/payroll/payroll.d";
import { type APPROVAL_GATES } from "./approval-types";
import { describeApprovalProvenance } from "./utils";

interface ApprovalToggleProps {
  gate: (typeof APPROVAL_GATES)[number];
  accessKey: IPayrollSheetApprovalAccessKey;
  isPending: boolean;
  isBusy: boolean;
  onChange?: (enabled: boolean) => void;
}

/**
 * One gate for one approval unit.
 *
 * Turning it ON submits immediately; turning it OFF routes through a
 * confirmation that can carry a reason, since withdrawing a sign-off invalidates
 * work other people may already have acted on. Left disabled rather than hidden
 * for a reader without approval access: they still need to see where the period
 * stands, they just can't move it.
 */
export const ApprovalToggle = ({
  gate,
  accessKey,
  isPending,
  isBusy,
  onChange,
}: ApprovalToggleProps) => {
  const { label } = gate;
  const approval = accessKey.approvals[gate.type];
  const isApproved = approval.approved;
  const canChange = accessKey.approval_authorized && Boolean(onChange);
  // The matrix has no room for a provenance line, so the hint carries it —
  // hovering a mark says who settled it without opening anything.
  const provenance = describeApprovalProvenance(approval);
  const action = canChange
    ? isApproved
      ? "Switch off to take it back."
      : "Switch on to approve."
    : "You can see these sheets, but you can't approve them.";
  const hint = [
    `${label}: ${isApproved ? "approved" : "not approved yet"}`,
    provenance,
    action,
  ]
    .filter(Boolean)
    .join(". ")
    .replace(/\.\./g, ".");

  return (
    <Stack
      direction="row"
      align="center"
      justify="end"
      gap="sm"
      // A fixed box so the spinner appearing beside one switch can't shuffle the
      // column it sits in.
      className="w-14 shrink-0"
      title={hint}
    >
      {isPending && (
        <Loader2
          aria-hidden="true"
          className="h-3.5 w-3.5 animate-spin text-interactive-text"
        />
      )}
      <Switch
        aria-label={`${label} for ${accessKey.name}`}
        checked={isApproved}
        disabled={!canChange || isBusy}
        onCheckedChange={(enabled) => onChange?.(enabled)}
        className={cn({ "cursor-default": !canChange })}
      />
    </Stack>
  );
};
